'use client';
import React, { useState, use } from 'react';
import BackgroundFX from '@/components/BackgroundFX';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import TopBar from '@/components/TopBar';
import Footer from '@/components/Footer';
import StatusBar from '@/components/StatusBar';
import SupportButton from '@/components/SupportButton';
import ShieldedBuyModal from '@/components/ShieldedBuyModal';
import Link from 'next/link';

const BASE = 'https://zecbit.net';

interface ItemPageProps {
  params: Promise<{ collection: string; id: string }>;
}

export default function ItemPage({ params }: ItemPageProps) {
  const { collection, id } = use(params);
  const numId = parseInt(id, 10);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'buy' | 'offer'>('buy');

  const traits = [
    {trait:'Background',value:'Golden Dusk',rarity:'12.4%'},
    {trait:'Body',value:'Pixel Warrior',rarity:'8.2%'},
    {trait:'Eyes',value:'Laser Red',rarity:'3.1%'},
    {trait:'Mouth',value:'Smirk',rarity:'15.7%'},
    {trait:'Accessory',value:'ZEC Chain',rarity:'5.9%'},
    {trait:'Hat',value:'Crown',rarity:'2.3%'},
  ];

  const openBuy = () => { setModalMode('buy'); setModalOpen(true); };
  const openOffer = () => { setModalMode('offer'); setModalOpen(true); };

  return (
    <>
      <BackgroundFX />
      <div className="shell">
        <MobileNav />
        <Sidebar />
        <div className="main">
          <TopBar />
          <main style={{padding:'var(--main-top) 0 90px'}}>
            <div style={{maxWidth:'var(--measure)',margin:'0 auto',width:'100%',padding:'clamp(14px,2vw,20px) clamp(14px,2.5vw,24px) clamp(20px,3vw,32px)',animation:'fadeIn var(--dur-3) var(--ease-out)'}}>
              {/* Breadcrumb */}
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20,fontSize:13,color:'var(--text-faint)'}}>
                <Link href="/" style={{color:'var(--text-faint)',transition:'color .15s'}}>Discover</Link>
                <span>›</span>
                <Link href={`/collection/${collection}`} style={{color:'var(--text-faint)',transition:'color .15s'}}>ZecBit Genesis</Link>
                <span>›</span>
                <span style={{color:'var(--text)'}}>#{id}</span>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,420px)',gap:34,alignItems:'start'}}>
                {/* Left: Art */}
                <div>
                  <div style={{borderRadius:16,overflow:'hidden',border:'1px solid rgba(228,228,229,.14)',background:'var(--bg-1)',aspectRatio:'1/1',maxWidth:520}}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={`${BASE}/api/art/${collection}/${id}`}
                      alt={`ZEC #${id}`}
                      style={{width:'100%',height:'100%',objectFit:'cover',imageRendering:'pixelated',display:'block'}}
                    />
                  </div>
                  {/* Traits */}
                  <div style={{marginTop:20}}>
                    <div style={{fontFamily:'var(--display)',fontWeight:600,fontSize:14,marginBottom:12}}>Traits</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:8}}>
                      {traits.map(t => (
                        <div key={t.trait} style={{background:'var(--bg-2)',border:'1px solid var(--line)',borderRadius:10,padding:'10px 12px'}}>
                          <div style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'1.2px',color:'var(--text-faint)',textTransform:'uppercase',marginBottom:4}}>{t.trait}</div>
                          <div style={{fontSize:13,fontWeight:500,color:'var(--text)',marginBottom:2}}>{t.value}</div>
                          <div style={{fontFamily:'var(--mono)',fontSize:10,color:'var(--gold)'}}>{t.rarity}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: Info */}
                <div style={{position:'sticky',top:'var(--stick-top)'}}>
                  <div style={{marginBottom:4}}>
                    <Link href={`/collection/${collection}`} style={{fontSize:13,color:'var(--text-faint)',transition:'color .15s'}}>ZecBit Genesis — Shielded</Link>
                  </div>
                  <h1 style={{fontFamily:'var(--display)',fontWeight:700,fontSize:'clamp(24px,2.5vw,34px)',letterSpacing:'-0.03em',margin:'0 0 4px'}}>ZEC #{id}</h1>
                  <div style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-faint)',marginBottom:20}}>Rank #{numId % 3333 + 1} of 3,333</div>

                  {/* Price panel */}
                  <div className="panel" style={{padding:20,marginBottom:16}}>
                    <div style={{fontFamily:'var(--mono)',fontSize:10,letterSpacing:'1.2px',color:'var(--text-faint)',marginBottom:8}}>CURRENT PRICE</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:28,fontWeight:600,color:'var(--text)',marginBottom:4}}>0.3499 ZEC</div>
                    <div style={{fontSize:12,color:'var(--text-faint)',marginBottom:20}}>~$394.82 USD</div>
                    <button
                      className="btn btn-gold"
                      style={{width:'100%',padding:'12px 16px',fontSize:14,marginBottom:8}}
                      onClick={openBuy}
                    >
                      Buy now
                    </button>
                    <button
                      className="btn"
                      style={{width:'100%',padding:'10px 16px',fontSize:13}}
                      onClick={openOffer}
                    >
                      Make offer
                    </button>
                  </div>

                  {/* On-chain proof */}
                  <div style={{background:'var(--bg-1)',border:'1px solid var(--line)',borderRadius:12,padding:'16px 18px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" aria-hidden="true">
                        <path d="M12 2l8 3.5V11c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V5.5z" />
                      </svg>
                      <span style={{fontFamily:'var(--display)',fontWeight:600,fontSize:13,color:'var(--gold)'}}>Committed on-chain</span>
                    </div>
                    <div style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'1.4px',color:'var(--text-faint)',textTransform:'uppercase',marginBottom:4}}>Collection</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-2)',marginBottom:12,wordBreak:'break-all'}}>zecbit-genesis</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'1.4px',color:'var(--text-faint)',textTransform:'uppercase',marginBottom:4}}>Token ID</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-2)',marginBottom:12}}>#{id}</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:9,letterSpacing:'1.4px',color:'var(--text-faint)',textTransform:'uppercase',marginBottom:4}}>Network</div>
                    <div style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-2)'}}>Zcash ZSA</div>
                  </div>
                </div>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>
      <StatusBar />
      <SupportButton />

      <ShieldedBuyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        itemName={`ZEC #${id}`}
        priceZec={modalMode === 'buy' ? '0.3499' : '0.28413'}
        priceUsd={modalMode === 'buy' ? '394.82' : '320.50'}
      />
    </>
  );
}
